"use client";

import { CSSProperties, ReactNode } from 'react';
import Link from 'next/link';

interface CardProps {
  children: ReactNode;
  href?: string;
  className?: string;
  style?: CSSProperties;
  borderTopColor?: string;
  borderTopWidth?: string;
}

/**
 * Reusable Card component with consistent styling and hover effects
 * Can be used as a Link (if href provided) or a regular div
 * Matches the service card styling from HomeClient
 */
export default function Card({ 
  children, 
  href, 
  className = '', 
  style = {},
  borderTopColor,
  borderTopWidth,
}: CardProps) {
  const baseStyle: CSSProperties = {
    backgroundColor: 'var(--bg-card)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
    borderColor: 'var(--border-primary)',
    ...(borderTopColor && { borderTopColor }),
    ...(borderTopWidth && { borderTopWidth }),
    ...style,
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.boxShadow = '0 15px 30px rgba(45, 212, 191, 0.2)';
    e.currentTarget.style.borderColor = 'var(--border-accent)';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.3)';
    e.currentTarget.style.borderColor = 'var(--border-primary)';
  };

  const baseClassName = `border transition-all duration-300 hover:-translate-y-2 ${className}`;

  if (href) {
    return (
      <Link
        href={href}
        className={baseClassName}
        style={baseStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Link>
    );
  }

  return (
    <div
      className={baseClassName}
      style={baseStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
}
